import { ReactNode, useMemo } from 'react';
import {
  IconAlertSquareRounded,
  IconBellRinging,
  IconBuilding,
  IconBuildingSkyscraper,
  IconClipboard,
  IconClipboardData,
  IconClockHour4,
  IconCloudDownload,
  IconCooker,
  IconDatabase,
  IconFileExport,
  IconForms,
  IconHome,
  IconHomeCog,
  IconHierarchy,
  IconLayoutDashboard,
  IconLayoutList,
  IconReceipt2,
  IconRulerMeasure,
  IconSettings,
  IconSignature,
  IconUser,
  IconUserCog,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import {
  FEATURE_TOGGLE_BROWSE_REVISION_TEMPLATES,
  FEATURE_TOGGLE_BULK_SIGN_REVISION,
  FEATURE_TOGGLE_CREATE_DEVICE_SUBTYPES,
  FEATURE_TOGGLE_EXPORT_PAGE,
  FEATURE_TOGGLE_REMOTE_SOURCES,
} from 'env';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import { DepartmentText, objectText, useDepartmentText } from 'model/DepartmentTerminology';
import { ROLE_ADMIN_ID } from 'model/Role';
import {
  DASHBOARD_PAGE_PATH,
  DEPARTMENTS_PAGE_PATH,
  DEVICES_PAGE_PATH,
  DEVICE_REVISION_PLANS_PAGE_PATH,
  DEVICE_TYPES_PAGE_PATH,
  EVIDENCE_FIELDS_PAGE_PATH,
  EVIDENCE_FIELD_SECTIONS_PAGE_PATH,
  EXPORT_PAGE_PATH,
  FAULTS_PAGE_PATH,
  FORMS_PAGE_PATH,
  GLOBAL_PREDEFINED_DATA_PAGE_PATH,
  MEASURING_DEVICES_PAGE_PATH,
  MY_COMPANY_SETTINGS_PAGE_PATH,
  NOTIFICATION_SETTINGS_PAGE_PATH,
  ORGANIZATIONS_PAGE_PATH,
  PRICE_OFFERS_PAGE_PATH,
  REMOTE_SOURCES_PAGE_PATH,
  REVISIONS_PAGE_PATH,
  REVISION_TEMPLATES_PAGE_PATH,
  SIGNATURES_PAGE_PATH,
  USERS_PAGE_PATH,
  USERS_PREDEFINED_DATA_PAGE_PATH,
  HR_PEOPLE_PAGE_PATH,
  HR_TERMS_PAGE_PATH,
  HR_TERM_TYPES_PAGE_PATH,
  HR_FILE_CATEGORIES_PAGE_PATH,
  HIERARCHIES_PAGE_PATH,
} from 'routes/paths';

export type DashboardNavigationSectionKey = 'dashboard' | 'passport' | 'hr' | 'structure' | 'settings' | 'admin';
export type DashboardNavigationGroupKey = Exclude<DashboardNavigationSectionKey, 'dashboard'>;

interface DashboardNavigationContextValue {
  departmentText?: DepartmentText;
  userId: number | null;
  roleId: number | null;
  priceOffersModuleEnabled: boolean;
  hrModuleEnabled: boolean;
  revisionsModuleEnabled: boolean;
  formsModuleEnabled: boolean;
  revisionTemplatesModuleEnabled: boolean;
  hasPermissionAnywhere: (permission: string) => boolean;
  hasPermissionGlobally: (permission: string) => boolean;
}

export interface DashboardNavigationItem {
  label: string;
  to: string;
  icon: ReactNode;
}

export interface DashboardNavigationSection {
  key: DashboardNavigationSectionKey;
  label: string;
  mobileLabel: string;
  icon: ReactNode;
  to?: string;
  items: DashboardNavigationItem[];
}

/**
 * Returns the active top-level navigation section for a pathname.
 */
export function getDashboardActiveSection(pathname: string): DashboardNavigationSectionKey | null {
  if (pathname === DASHBOARD_PAGE_PATH.original) {
    return 'dashboard';
  }

  if (
    pathname.startsWith('/devices') ||
    pathname.startsWith('/revisions') ||
    pathname.startsWith('/forms') ||
    pathname.startsWith('/signatures') ||
    pathname.startsWith('/revision-plans') ||
    pathname.startsWith('/price-offers') ||
    pathname.startsWith('/faults')
  ) {
    return 'passport';
  }

  if (pathname.startsWith('/hr')) {
    return 'hr';
  }

  if (
    pathname.startsWith('/departments') ||
    pathname.startsWith('/users') ||
    pathname.startsWith('/organizations') ||
    pathname.startsWith('/hierarchies')
  ) {
    return 'structure';
  }

  if (
    pathname.startsWith('/device-types') ||
    pathname.startsWith('/predefined-data/user') ||
    pathname.startsWith('/predefined-data/global') ||
    pathname.startsWith('/measuring-devices') ||
    pathname.startsWith('/my-company-settings') ||
    pathname.startsWith('/notifications-settings') ||
    pathname.startsWith('/revision-templates')
  ) {
    return 'settings';
  }

  if (
    pathname.startsWith('/export') ||
    pathname.startsWith('/evidence-field-sections') ||
    pathname.startsWith('/evidence-fields') ||
    pathname.startsWith('/remote-sources')
  ) {
    return 'admin';
  }

  return null;
}

/**
 * Builds the visible dashboard navigation from the current API context.
 */
export function getDashboardNavigation({
  departmentText = objectText,
  userId,
  roleId,
  priceOffersModuleEnabled,
  hrModuleEnabled,
  revisionsModuleEnabled,
  formsModuleEnabled,
  revisionTemplatesModuleEnabled,
  hasPermissionAnywhere,
  hasPermissionGlobally,
}: DashboardNavigationContextValue): DashboardNavigationSection[] {
  const sections: DashboardNavigationSection[] = [
    {
      key: 'dashboard',
      label: 'Riadiaci panel',
      mobileLabel: 'Panel',
      icon: <IconLayoutDashboard stroke="1.5" />,
      to: DASHBOARD_PAGE_PATH.original,
      items: [],
    },
  ];

  const passportItems: DashboardNavigationItem[] = [];

  if (hasPermissionAnywhere('browse-devices')) {
    passportItems.push({
      label: 'Zariadenia',
      to: DEVICES_PAGE_PATH.original,
      icon: <IconCooker stroke="1.5" />,
    });
  }

  if (revisionsModuleEnabled && hasPermissionAnywhere('*-revisions')) {
    passportItems.push({
      label: 'Revízne správy',
      to: REVISIONS_PAGE_PATH.original,
      icon: <IconClipboard stroke="1.5" />,
    });
  }

  if (formsModuleEnabled && hasPermissionAnywhere('*-forms')) {
    passportItems.push({
      label: 'Formuláre',
      to: FORMS_PAGE_PATH.original,
      icon: <IconForms stroke="1.5" />,
    });
  }

  if (
    FEATURE_TOGGLE_BULK_SIGN_REVISION &&
    ((revisionsModuleEnabled && hasPermissionAnywhere('fill-out-revisions')) ||
      (formsModuleEnabled && hasPermissionAnywhere('fill-out-forms')))
  ) {
    passportItems.push({
      label: 'Podpisovanie',
      to: SIGNATURES_PAGE_PATH.original,
      icon: <IconSignature stroke="1.5" />,
    });
  }

  if (revisionsModuleEnabled && hasPermissionAnywhere('browse-devices')) {
    passportItems.push({
      label: 'Sledovanie termínov',
      to: DEVICE_REVISION_PLANS_PAGE_PATH.insert({ tabName: 'table' }),
      icon: <IconClockHour4 stroke="1.5" />,
    });
  }

  if (hasPermissionAnywhere('browse-devices')) {
    passportItems.push({
      label: 'Nedostatky',
      to: FAULTS_PAGE_PATH.original,
      icon: <IconAlertSquareRounded stroke="1.5" />,
    });
  }

  if (
    priceOffersModuleEnabled &&
    (hasPermissionAnywhere('assign-price-offers') || hasPermissionAnywhere('provide-price-offers'))
  ) {
    passportItems.push({
      label: 'Cenové ponuky',
      to: PRICE_OFFERS_PAGE_PATH.original,
      icon: <IconReceipt2 stroke="1.5" />,
    });
  }

  if (passportItems.length > 0) {
    sections.push({
      key: 'passport',
      label: 'Passport',
      mobileLabel: 'Passport',
      icon: <IconHomeCog stroke="1.5" />,
      items: passportItems,
    });
  }

  if (hrModuleEnabled && hasPermissionAnywhere('browse-hr')) {
    const hrItems: DashboardNavigationItem[] = [
      {
        label: 'Osoby',
        to: HR_PEOPLE_PAGE_PATH.original,
        icon: <IconUser stroke="1.5" />,
      },
      {
        label: 'Termíny',
        to: HR_TERMS_PAGE_PATH.insert({ tab: 'table' }),
        icon: <IconClockHour4 stroke="1.5" />,
      },
    ];

    if (roleId === ROLE_ADMIN_ID) {
      hrItems.push(
        {
          label: 'Typy termínov',
          to: HR_TERM_TYPES_PAGE_PATH.original,
          icon: <IconSettings stroke="1.5" />,
        },
        {
          label: 'Kategórie súborov',
          to: HR_FILE_CATEGORIES_PAGE_PATH.original,
          icon: <IconFileExport stroke="1.5" />,
        }
      );
    }

    sections.push({
      key: 'hr',
      label: 'Ľudské zdroje',
      mobileLabel: 'Ľudské zdroje',
      icon: <IconUsersGroup stroke="1.5" />,
      items: hrItems,
    });
  }

  const structureItems: DashboardNavigationItem[] = [];

  if (hasPermissionGlobally('manage-organizations')) {
    structureItems.push({
      label: 'Organizácie',
      to: ORGANIZATIONS_PAGE_PATH.original,
      icon: <IconBuildingSkyscraper stroke="1.5" />,
    });
  }

  if (hasPermissionGlobally('manage-hierarchies')) {
    structureItems.push({
      label: 'Hierarchie',
      to: HIERARCHIES_PAGE_PATH.original,
      icon: <IconHierarchy stroke="1.5" />,
    });
  }

  if (hasPermissionAnywhere('manage-departments')) {
    structureItems.push({
      label: departmentText('Položky úrovne', 'Strediská'),
      to: DEPARTMENTS_PAGE_PATH.original,
      icon: <IconHome stroke="1.5" />,
    });
  }

  if (hasPermissionAnywhere('manage-users')) {
    structureItems.push({
      label: 'Používatelia',
      to: USERS_PAGE_PATH.original,
      icon: <IconUser stroke="1.5" />,
    });
  }

  if (structureItems.length > 0) {
    sections.push({
      key: 'structure',
      label: 'Štruktúra',
      mobileLabel: 'Štruktúra',
      icon: <IconBuilding stroke="1.5" />,
      items: structureItems,
    });
  }

  const settingsItems: DashboardNavigationItem[] = [];

  if (FEATURE_TOGGLE_CREATE_DEVICE_SUBTYPES && hasPermissionAnywhere('define-device-subtypes')) {
    settingsItems.push({
      label: 'Typy zariadení',
      to: DEVICE_TYPES_PAGE_PATH.original,
      icon: <IconCooker stroke="1.5" />,
    });
  }

  if (
    (revisionsModuleEnabled && hasPermissionAnywhere('fill-out-revisions')) ||
    (formsModuleEnabled && hasPermissionAnywhere('fill-out-forms'))
  ) {
    settingsItems.push({
      label: 'Predvolené údaje',
      to: USERS_PREDEFINED_DATA_PAGE_PATH.original,
      icon: <IconDatabase stroke="1.5" />,
    });
    settingsItems.push({
      label: 'Meracie prístroje',
      to: MEASURING_DEVICES_PAGE_PATH.original,
      icon: <IconRulerMeasure stroke="1.5" />,
    });
  }

  if (revisionsModuleEnabled && hasPermissionAnywhere('fill-out-revisions')) {
    settingsItems.push({
      label: 'Nastavenia spoločnosti',
      to: MY_COMPANY_SETTINGS_PAGE_PATH.original,
      icon: <IconBuildingSkyscraper stroke="1.5" />,
    });
  }

  if (
    (hasPermissionAnywhere('*-devices') ||
      (revisionsModuleEnabled && hasPermissionAnywhere('*-revisions')) ||
      (hrModuleEnabled && hasPermissionAnywhere('browse-hr'))) &&
    userId
  ) {
    settingsItems.push({
      label: 'Nastavenia upozornení',
      to: NOTIFICATION_SETTINGS_PAGE_PATH.insert({ userId }),
      icon: <IconBellRinging stroke="1.5" />,
    });
  }

  if (
    FEATURE_TOGGLE_BROWSE_REVISION_TEMPLATES &&
    revisionTemplatesModuleEnabled &&
    hasPermissionAnywhere('manage-templates')
  ) {
    settingsItems.push({
      label: 'Šablóny revíznych správ',
      to: REVISION_TEMPLATES_PAGE_PATH.original,
      icon: <IconClipboardData stroke="1.5" />,
    });
  }

  if (hasPermissionGlobally('manage-global-predefined-data')) {
    settingsItems.push({
      label: 'Globálne predvol. údaje',
      to: GLOBAL_PREDEFINED_DATA_PAGE_PATH.original,
      icon: <IconDatabase stroke="1.5" />,
    });
  }

  if (settingsItems.length > 0) {
    sections.push({
      key: 'settings',
      label: 'Nastavenia',
      mobileLabel: 'Nastavenia',
      icon: <IconSettings stroke="1.5" />,
      items: settingsItems,
    });
  }

  if (roleId === ROLE_ADMIN_ID) {
    const adminItems: DashboardNavigationItem[] = [];

    adminItems.push({
      label: 'Evidenčné polia',
      to: EVIDENCE_FIELDS_PAGE_PATH.original,
      icon: <IconForms stroke="1.5" />,
    });

    adminItems.push({
      label: 'Sekcie polí',
      to: EVIDENCE_FIELD_SECTIONS_PAGE_PATH.original,
      icon: <IconLayoutList stroke="1.5" />,
    });

    if (FEATURE_TOGGLE_EXPORT_PAGE) {
      adminItems.push({
        label: 'Systémový export',
        to: EXPORT_PAGE_PATH.original,
        icon: <IconFileExport stroke="1.5" />,
      });
    }

    if (FEATURE_TOGGLE_REMOTE_SOURCES) {
      adminItems.push({
        label: 'Externé zdroje',
        to: REMOTE_SOURCES_PAGE_PATH.original,
        icon: <IconCloudDownload stroke="1.5" />,
      });
    }

    sections.push({
      key: 'admin',
      label: 'Admin nastavenia',
      mobileLabel: 'Admin',
      icon: <IconUserCog stroke="1.5" />,
      items: adminItems,
    });
  }

  return sections;
}

/**
 * Returns the visible dashboard navigation for the current user.
 */
export function useDashboardNavigation() {
  const departmentText = useDepartmentText();
  const { userId, roleId, hasPermissionAnywhere, hasPermissionGlobally } = useApi();
  const { priceOffers, hr, revisions, forms, revisionTemplates } = useOrganizationModules();

  return useMemo(
    () =>
      getDashboardNavigation({
        departmentText,
        userId,
        roleId,
        priceOffersModuleEnabled: priceOffers,
        hrModuleEnabled: hr,
        revisionsModuleEnabled: revisions,
        formsModuleEnabled: forms,
        revisionTemplatesModuleEnabled: revisionTemplates,
        hasPermissionAnywhere,
        hasPermissionGlobally,
      }),
    [
      departmentText,
      hasPermissionAnywhere,
      hasPermissionGlobally,
      hr,
      priceOffers,
      revisions,
      forms,
      revisionTemplates,
      roleId,
      userId,
    ]
  );
}
